import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { CSSTransition } from 'react-transition-group';

// A dropdown control component.
// children: ReactNode - The children of the DropdownControl.
// dropdown: ReactNode - The dropdown content.
// open: boolean - The state of the dropdown.
// dropdownHeight: number - The height of the dropdown.
// placement: "left" | "right" | "center" - The placement of the dropdown.
// handleBlur: () => void - The blur event handler.
// clickAndClose: boolean - If true, the dropdown will close when the user clicks on the dropdown.
// props: any - The props of the DropdownControl.

const DropdownControl = ({ children, dropdown, open, dropdownHeight, placement, handleBlur, clickAndClose = true, ...props }) => {
  const [dropdownLocation, setDropdownLocation] = useState("bottom");
  const [isChildClicked, setIsChildClicked] = useState(false);
  const dropdownControlContainerRef = useRef(null);
  const nodeRef = React.useRef(null);

  const _handleBlur = () => {
    if (clickAndClose || !isChildClicked) {
      handleBlur();
    } else {
      setIsChildClicked(false);
    }
  };

  useEffect(() => {
    if (dropdownControlContainerRef.current) {
      const rect = dropdownControlContainerRef.current.getBoundingClientRect();
      const absoluteTop = rect.top + window.scrollY;
      const dropdownHeight = nodeRef?.current?.offsetHeight ?? 0;
      // console.log(document.documentElement.scrollHeight, absoluteTop, dropdownHeight)
      setDropdownLocation(document.documentElement.scrollHeight - absoluteTop - 50 > dropdownHeight ? "bottom" : "top");
    }
  }, [open]);

  return (
    <DropdownControlContainer onBlur={handleBlur ? _handleBlur : null} ref={dropdownControlContainerRef} {...props}>
      {children}

      {
        dropdown &&
        <CSSTransition
          nodeRef={nodeRef}
          in={open}
          timeout={250}
          classNames="fade"
          unmountOnExit
        >
          <Dropdown
            ref={nodeRef}
            $dropdownHeight={dropdownHeight}
            $dropdownLocation={dropdownLocation}
            $placement={placement}
            onBlur={handleBlur ? _handleBlur : null}
            onMouseDown={() => { setIsChildClicked(true) }}
            onMouseUp={() => { setIsChildClicked(false) }}
            tabIndex={"-1"} // This is to make the dropdown focusable, so that we can blur the dropdown when the user clicks outside of the dropdown.
          >
            {dropdown}
          </Dropdown>
        </CSSTransition>
      }
    </DropdownControlContainer>
  );
};

const DropdownControlContainer = styled.div`
  position: relative;
`;

const Dropdown = styled.div`
  width: max-content;
  bottom: ${props => props.$dropdownLocation === "top" ? "45px" : "auto"};
  ${props => props.$placement === "left" && "left: 0px;"}
  ${props => props.$placement === "right" && "right: 0px;"}
  ${props => props.$placement === "center" && "left: 50%; transform: translateX(-50%);"}
  margin: 5px 0px 0px;
  position: absolute;
  padding: 8px;
  box-shadow: 0px 0px 0.5rem 0px rgba(2, 114, 174, 0.2);
  border-radius: 0.5rem;
  background-color: var(--aia-bx-white);
  ${props => props.$dropdownHeight && `max-height: ${props.$dropdownHeight}px;`}
  z-index: 100;
`;

export default DropdownControl;